import { useQuery } from "@tanstack/react-query";
import useApiRequest from "./useApiRequest";
import { HealthcareCompany, HealthcareCompanyType } from "@shared/types/commonTypes";

export interface HookGetMyCompaniesOptions {
  type?: HealthcareCompanyType;
  enabled?: boolean;
}

type HookGetMyCompaniesResult = {
  companies: HealthcareCompany[] | undefined;
  error: Error | null;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
};

/**
 * Hook to fetch healthcare companies (clinics/event organizers).
 * Can filter by company type if provided.
 */
export const useGetMyCompanies = (
  options: HookGetMyCompaniesOptions = {}
): HookGetMyCompaniesResult => {
  const { GET } = useApiRequest();
  const { type, enabled = true } = options;

  const fetchHealthcareCompanies = async (): Promise<HealthcareCompany[]> => {
    const url = type
      ? `/api/healthcare-companies?type=${type}`
      : `/api/healthcare-companies`;
    
    const companiesData: HealthcareCompany[] = await GET(url);
    return companiesData;
  };

  const queryKey = type ? ["healthcare-companies", type] : ["healthcare-companies"];

  const { data, error, isLoading, isFetching, isError } = useQuery<
    HealthcareCompany[],
    Error
  >({
    queryKey,
    queryFn: fetchHealthcareCompanies,
    enabled,
  });

  return {
    companies: data,
    error: error ?? null,
    isLoading,
    isFetching,
    isError,
  } satisfies HookGetMyCompaniesResult;
};
